import React, { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Store, MinusCircle, X, ChevronDown } from "lucide-react";
import i18n from "../../i18n/index.js";
import useAuth from "../../hooks/useAuth";
import { getActiveClientsApi } from "../../api/company";
import { cn } from "../../utils/cn";

/**
 * Autocomplete selector for choosing a Company (commerce). Used in:
 *   - Conversation side panel (assign / change override)
 *   - Helpdesk filter bar (filter conversations by commerce)
 *   - Admin contacts page (set default commerce per contact)
 *
 * Reuses the same endpoint as the global "Filtrar y ordenar" modal: returns
 * the full list of active-contract clients for the logistic company, cached
 * per session and filtered client-side as the agent types.
 *
 * The dropdown is absolutely positioned over surrounding content so it does
 * not push siblings down.
 *
 * Props:
 *   - value: { id, name } | null
 *   - onChange(commerce | null)
 *   - placeholder
 *   - allowClear (default true)      show "sin asignar" option
 *   - autoFocus
 *   - size: 'sm' | 'md'
 */

// Module-level cache so multiple CommerceSelector instances don't refetch.
const clientCache = new Map();
const CACHE_TTL_MS = 5 * 60 * 1000;

function getActiveClients(lcid) {
  const now = Date.now();
  const entry = clientCache.get(lcid);
  if (entry && entry.expiresAt > now) return entry.promise;

  const promise = getActiveClientsApi(lcid).then((res) => {
    if (!Array.isArray(res)) return [];
    return res.map((c) => ({
      id: c?._id,
      name: c?.name || i18n.t("Sin nombre"),
      businessIdentifier: c?.businessIdentifier,
    }));
  });
  clientCache.set(lcid, { promise, expiresAt: now + CACHE_TTL_MS });
  return promise;
}

export function invalidateCommerceCache(lcid) {
  if (lcid) clientCache.delete(lcid);
  else clientCache.clear();
}

export default function CommerceSelector({
  value,
  onChange,
  placeholder = i18n.t("Buscar comercio…"),
  allowClear = true,
  autoFocus = false,
  size = "md",
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const logisticCompanyId =
    user?.logisticCompanyId?._id ||
    user?.logisticCompanyId ||
    "";

  const [allClients, setAllClients] = useState([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [highlight, setHighlight] = useState(-1);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listRef = useRef(null);

  useEffect(() => {
    if (!logisticCompanyId) return;
    setLoading(true);
    getActiveClients(logisticCompanyId)
      .then((list) => setAllClients(list))
      .catch(() => setAllClients([]))
      .finally(() => setLoading(false));
  }, [logisticCompanyId]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return allClients.slice(0, 50);
    return allClients
      .filter((c) => {
        const nameMatch = c.name?.toLowerCase().includes(q);
        const idMatch = c.businessIdentifier?.toLowerCase?.().includes(q);
        return nameMatch || idMatch;
      })
      .slice(0, 50);
  }, [allClients, query]);

  // Click outside → close
  useEffect(() => {
    function onDocClick(e) {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
      }
    }
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  // Mantener el item resaltado visible al navegar con flechas
  useEffect(() => {
    if (!open || highlight < 0 || !listRef.current) return;
    const el = listRef.current.querySelector(`[data-idx="${highlight}"]`);
    if (el && typeof el.scrollIntoView === "function") {
      el.scrollIntoView({ block: "nearest" });
    }
  }, [highlight, open]);

  const select = (commerce) => {
    onChange?.(commerce);
    setQuery("");
    setOpen(false);
    setHighlight(-1);
  };

  const handleKeyDown = (e) => {
    if (!open) {
      if (e.key === "ArrowDown" || e.key === "Enter") {
        setOpen(true);
        e.preventDefault();
      }
      return;
    }
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setHighlight((h) => Math.min(h + 1, filtered.length - 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setHighlight((h) => Math.max(h - 1, allowClear ? -1 : 0));
    } else if (e.key === "Enter") {
      e.preventDefault();
      if (highlight === -1 && allowClear) {
        select(null);
      } else if (highlight >= 0 && filtered[highlight]) {
        select(filtered[highlight]);
      }
    } else if (e.key === "Escape") {
      e.preventDefault();
      setOpen(false);
    }
  };

  const isSm = size === "sm";
  // sm (inbox filter row): 40px touch / 32px desktop and 13px text, matching
  // the label filter next to it (second visual pass; it was 28.8px / 10.8px).
  const triggerHeight = isSm ? "h-[40px] md:h-[32px]" : "h-9";
  const triggerText = isSm ? "text-[13px]" : "text-sm";

  // Trigger: chip (cuando hay value y no está abierto) o input (cuando abierto o sin value)
  const showChip = value && !open;

  return (
    <div ref={containerRef} className="relative w-full">
      {showChip ? (
        <button
          type="button"
          onClick={() => {
            setOpen(true);
            setTimeout(() => inputRef.current?.focus(), 0);
          }}
          className={cn(
            "w-full inline-flex items-center gap-1.5 px-2.5 rounded-md border border-border bg-bg text-fg",
            "hover:bg-bg-muted hover:border-fg-subtle transition-colors text-left",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-500",
            triggerHeight,
            triggerText,
          )}
          title={value.name}
        >
          {/* Ícono lucide DIMENSIONADO (w-4/h-4). Nunca material-symbols: sin
              dimensionar, el glifo cae a tamaño de icon-font (24px) y pisa el
              label — el mismo bug que ya se corrigió en CommerceBadge. */}
          <Store className="w-4 h-4 text-brand-600 shrink-0" aria-hidden="true" />
          <span className="flex-1 truncate">{value.name}</span>
          {allowClear && (
            <span
              role="button"
              tabIndex={-1}
              aria-label={t("Quitar comercio")}
              onClick={(e) => {
                e.stopPropagation();
                onChange?.(null);
              }}
              className="inline-flex w-4 h-4 items-center justify-center rounded text-fg-subtle hover:text-fg hover:bg-bg-subtle"
            >
              <X className="w-3 h-3" aria-hidden="true" />
            </span>
          )}
          <ChevronDown className="w-3.5 h-3.5 text-fg-subtle shrink-0" aria-hidden="true" />
        </button>
      ) : (
        <div className="relative">
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
              setHighlight(-1);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder={value?.name || placeholder}
            autoFocus={autoFocus}
            autoComplete="off"
            aria-autocomplete="list"
            aria-expanded={open}
            aria-controls="commerce-selector-listbox"
            role="combobox"
            className={cn(
              // pl-8: el ícono ocupa hasta 24px (left-2 + w-4); 32px deja 8px
              // de aire real entre glifo y texto ("Todos" ya no queda pegado).
              //
              // Los `!` NO son cosméticos: scss/_modern-forms.scss declara
              //   input[type="text"] { padding: .625rem 1rem; font-size: 1rem }
              // y un selector tipo+atributo pesa (0,1,1) — más que la utilidad
              // (0,1,0). Sin la marca el padding real caía a 16px y el glifo de
              // Store terminaba PISANDO el texto (medido: -7px de solape con
              // "Todos", y -5px del chevron por la derecha); además la
              // tipografía subía a 16px y rompía la alineación con el buscador
              // de arriba. El alto lo fija triggerHeight, así que el padding
              // vertical del scss también sobra: !py-0 centra de verdad.
              // placeholder fg-muted, not fg-subtle: fg-subtle on bg is 2.68:1.
              "w-full !pl-8 !pr-7 !py-0 rounded-md border border-border bg-bg text-fg placeholder:text-fg-muted",
              "focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500",
              triggerHeight,
              isSm ? "!text-[13px]" : "!text-sm",
            )}
          />
          <Store
            className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-fg-muted pointer-events-none"
            aria-hidden="true"
          />
          <ChevronDown
            className="absolute right-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-fg-subtle pointer-events-none"
            aria-hidden="true"
          />
        </div>
      )}

      {/* Dropdown flotante: absolute sobre los siblings para no empujar el layout */}
      {open && (
        <ul
          ref={listRef}
          id="commerce-selector-listbox"
          role="listbox"
          className={cn(
            "absolute z-50 left-0 right-0 mt-1 max-h-72 overflow-y-auto bg-bg border border-border rounded-md shadow-elevated py-1",
            // sm sits in a half-width filter cell (~150px): the list needs room.
            isSm && "min-w-[240px]",
          )}
        >
          {allowClear && (
            <li
              role="option"
              aria-selected={highlight === -1}
              data-idx={-1}
              onMouseEnter={() => setHighlight(-1)}
              onClick={() => select(null)}
              className={cn(
                "flex items-center gap-2 px-3 h-8 cursor-pointer text-xs",
                highlight === -1
                  ? "bg-bg-muted text-fg"
                  : "text-fg-muted hover:bg-bg-muted/60",
              )}
            >
              <MinusCircle className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
              <span className="flex-1">{t("Sin asignar")}</span>
              {!value && (
                // Micro-label del sistema: 11px / 600 / uppercase / tracking-wider.
                // El 10px queda desterrado como rol de texto (ilegible).
                <span className="text-[11px] uppercase tracking-wider text-brand-600 font-semibold">
                  {t("Actual")}
                </span>
              )}
            </li>
          )}

          {loading && (
            <li className="px-3 py-2 text-xs text-fg-muted">{t("Cargando comercios…")}</li>
          )}

          {!loading && filtered.length === 0 && (
            <li className="px-3 py-2 text-xs text-fg-muted">
              {query.trim() ? t("Sin resultados") : t("No hay comercios disponibles")}
            </li>
          )}

          {!loading &&
            filtered.map((c, i) => {
              const isSelected = value?.id === c.id;
              const isHighlighted = highlight === i;
              return (
                <li
                  key={c.id}
                  role="option"
                  aria-selected={isSelected}
                  data-idx={i}
                  onMouseEnter={() => setHighlight(i)}
                  onClick={() => select(c)}
                  className={cn(
                    "flex items-center gap-2 px-3 h-8 cursor-pointer text-xs",
                    isHighlighted
                      ? "bg-brand-50 text-brand-900"
                      : isSelected
                      ? "bg-bg-muted text-fg font-medium"
                      : "text-fg hover:bg-bg-muted/60",
                  )}
                >
                  <span className="flex-1 truncate" title={c.name}>
                    {c.name}
                  </span>
                  {c.businessIdentifier && (
                    <span className="text-[11px] text-fg-muted font-mono shrink-0">
                      {c.businessIdentifier}
                    </span>
                  )}
                  {isSelected && (
                    <span className="text-[11px] uppercase tracking-wider text-brand-600 font-semibold shrink-0">
                      {t("Actual")}
                    </span>
                  )}
                </li>
              );
            })}
        </ul>
      )}
    </div>
  );
}
